/**
 * Utility to check token expiration and handle token refresh
 */

import { SuccessTokenStepResponse } from '@shared/services';

export class TokenExpirationChecker {
  /**
   * Check if a token is expired or will expire soon
   * @param tokenResponse The token response to check
   * @param bufferSeconds Buffer time in seconds before expiration (default: 30)
   * @returns true if token is expired or will expire soon
   */
  static isTokenExpired(tokenResponse: SuccessTokenStepResponse | undefined, bufferSeconds: number = 30): boolean {
    if (!tokenResponse) {
      console.log('[TokenExpirationChecker] No token response provided');
      return true;
    }
    if (!tokenResponse.expiration_time) {
      console.log('[TokenExpirationChecker] No expiration time in token response');
      return true;
    }

    const now = Math.floor(Date.now() / 1000);
    const expirationTime = tokenResponse.expiration_time;
    const bufferTime = now + bufferSeconds;
    const isExpired = bufferTime >= expirationTime;

    if (process.env.NODE_ENV === 'development') {
      console.log('[TokenExpirationChecker] Token expiration check:', {
        now,
        expirationTime,
        bufferTime,
        isExpired,
        timeUntilExpiration: expirationTime - now,
      });
    }

    return isExpired;
  }

  /**
   * Get the time until token expiration in seconds
   * @param tokenResponse The token response to check
   * @returns Time until expiration in seconds, or 0 if expired
   */
  static getTimeUntilExpiration(tokenResponse: SuccessTokenStepResponse | undefined): number {
    if (!tokenResponse || !tokenResponse.expiration_time) {
      return 0;
    }

    const now = Math.floor(Date.now() / 1000);
    const expirationTime = tokenResponse.expiration_time;
    
    return Math.max(0, expirationTime - now);
  }

  private static formatHoursMinutes(hours: number, minutes: number): string {
    const minutePart = minutes > 0 ? ` ${minutes}m` : '';
    return `${hours}h${minutePart}`;
  }

  private static formatMinutesSeconds(minutes: number, seconds: number): string {
    const secondPart = seconds > 0 ? ` ${seconds}s` : '';
    return `${minutes}m${secondPart}`;
  }

  /**
   * Get a human-readable time until expiration
   * @param tokenResponse The token response to check
   * @returns Human-readable time string
   */
  static getTimeUntilExpirationString(tokenResponse: SuccessTokenStepResponse | undefined): string {
    const totalSeconds = this.getTimeUntilExpiration(tokenResponse);
    if (totalSeconds === 0) return 'expired';

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return this.formatHoursMinutes(hours, minutes);
    if (minutes > 0) return this.formatMinutesSeconds(minutes, seconds);
    return `${seconds} seconds`;
  }

  /**
   * Check if token needs refresh (expires within buffer time)
   * @param tokenResponse The token response to check
   * @param bufferSeconds Buffer time in seconds (default: 60)
   * @returns true if token needs refresh
   */
  static needsRefresh(tokenResponse: SuccessTokenStepResponse | undefined, bufferSeconds: number = 60): boolean {
    if (!tokenResponse) {
      return true;
    }

    const timeUntilExpiration = this.getTimeUntilExpiration(tokenResponse);
    return timeUntilExpiration <= bufferSeconds;
  }
}
